import React, { FC, useContext, useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

import throwError from '../../../utils/error/_throwError';
import getWrappedDisplayName from '../../../utils/hocs/getWrappedDisplayName';
import get from '../../../utils/accessors/get';

import FormContext from '../FormContext';
import FormColContext from '../_FormColContext';
import { ControlComposedProps } from './Control';
import FieldGroupContext from '../_FieldGroupContext';

const withControlFormContext = <
  Value,
  StateValue,
  E extends Element = HTMLInputElement
>(WrappedComponent: FC<ControlComposedProps<Value, StateValue, E>>) => {
  const WithControlFormContext = (props: ControlComposedProps<Value, StateValue, E>) => {
    const { name, defaultValue } = props;

    const {
      registerControl, unregisterControl, initialValues, formScope, ancestorValuesDst, ancestorFormReducerScope, formReducerKey,
      setControlValue, setControlState, parentDefaultValues, formReducerScope, ancestorFormReducerKey, hideErrorMessages, allowInvalid, hideHoistedAnchor, hideErrorHoistedAnchor, hideWarningHoistedAnchor, hideCautionHoistedAnchor, hideInfoHoistedAnchor,
    } = useContext(FormContext);

    const { label } = useContext(FormColContext);

    const { grouped } = useContext(FieldGroupContext);

    const initialValue = useMemo(() => get(initialValues, name) as StateValue, [initialValues, name]);

    const resolvedDefaultValue = useMemo(() => (typeof defaultValue !== 'undefined') ? defaultValue : get(parentDefaultValues, name), [defaultValue, name, parentDefaultValues]);

    if (!registerControl) {
      throwError('Control: This component must be placed inside a "Form".');
    }

    return (
      <WrappedComponent
        registerControl={registerControl}
        unregisterControl={unregisterControl}
        initialValue={initialValue}
        formScope={formScope}
        ancestorFormReducerKey={ancestorFormReducerKey}
        ancestorFormReducerScope={ancestorFormReducerScope}
        ancestorValuesDst={ancestorValuesDst}
        formReducerKey={formReducerKey}
        setControlValue={setControlValue}
        setControlState={setControlState}
        colLabel={label}
        formReducerScope={formReducerScope}
        hideErrorMessages={hideErrorMessages}
        allowInvalid={allowInvalid}
        hideHoistedAnchor={hideHoistedAnchor}
        hideErrorHoistedAnchor={hideErrorHoistedAnchor}
        hideWarningHoistedAnchor={hideWarningHoistedAnchor}
        hideCautionHoistedAnchor={hideCautionHoistedAnchor}
        hideInfoHoistedAnchor={hideInfoHoistedAnchor}
        grouped={grouped}
        {...props}
        defaultValue={resolvedDefaultValue}
      />
    );
  };

  WithControlFormContext.displayName = getWrappedDisplayName('WithControlFormContext', WrappedComponent);

  return hoistNonReactStatics(WithControlFormContext, WrappedComponent);
};

export default withControlFormContext;
